import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'
import { HolidayType, IHoliday, ILocationHolidayUpdatedEvent } from '@vacationtracker/shared/types/holidays'
import { IHolidaysByYear } from '../../types/locations'

dayjs.extend(utc)

export type HolidayLength = 'SINGLE_DAY' | 'MULTI_DAY' | 'HALF_DAY'

export type HolidayField = 'name' | 'dates' | 'recurring'

export type HolidayTag = HolidayType | 'custom'

export const LIBRARY_TYPES: HolidayType[] = ['public', 'bank', 'school', 'optional', 'observance']

const CUSTOM_RECURRING_WARNING = 'components.createHolidaysForm.holidayCustomRecurringWarning'
const LIB_WARNING = 'components.createHolidaysForm.holidayFormRecurrenceDisabledWarning'
const NO_ID = 'holidaysTab.noId'
const IMPORTED_RECURRENCE_LOCKED = 'holidaysTab.recurrence.importedLocked'
const PAST_YEAR_NO_REPEAT = 'holidaysTab.recurrence.pastYearNoRepeat'

/** The backend's year boundaries are UTC; the reader's own Jan 1 can fall a day either side. */
export const utcToday = (): dayjs.Dayjs => dayjs.utc()

// The id alone repeats: an edited holiday can carry one id on several rows.
export const rowKey = (row: Pick<HolidayRow, 'id' | 'date'>): string => `${row.id}:${holidayDay(row.date)}`

export interface HolidayRow extends IHoliday {
  endDate?: string
}

export interface HolidayRowsByYear extends Omit<IHolidaysByYear, 'holidays'> {
  holidays: HolidayRow[]
}

export interface HolidayPatch {
  name?: string
  startDate?: string
  endDate?: string
  length?: HolidayLength
  recurring?: boolean
}

export interface HolidayUpdatePayloadInput {
  row: HolidayRow
  locationId: string
  patch: HolidayPatch
}

export type HolidayUpdatePayload = Pick<
  ILocationHolidayUpdatedEvent,
  'eventType' | 'eventGroup' | 'id' | 'locationId' | 'name' | 'startDate' | 'endDate' | 'recurring' | 'recurringRules' | 'transferIfFallsOnNonWorkingDay'
> & { isHalfDay: boolean }

export interface YearRange {
  first: number
  last: number
}

const EARLIEST_HOLIDAY_YEAR = 2020

export function holidayLength(row: HolidayRow): HolidayLength {
  if (row.endDate && holidayDay(row.endDate) !== holidayDay(row.date)) return 'MULTI_DAY'
  return row.isHalfDay ? 'HALF_DAY' : 'SINGLE_DAY'
}

export function holidayYears(byYear: IHolidaysByYear[]): number[] {
  return byYear
    .map((entry) => Number(entry.year))
    .filter((year) => Number.isFinite(year))
    .sort((a, b) => a - b)
}

export function holidaysForYear(byYear: HolidayRowsByYear[], year: number): HolidayRow[] {
  const entry = byYear.find((candidate) => Number(candidate.year) === year)
  return [...(entry?.holidays ?? [])].sort((a, b) => a.date.localeCompare(b.date))
}

export const rowYear = (row: Pick<HolidayRow, 'date'>): number => Number(row.date.slice(0, 4))

// Mirrors what location-year-holiday-updated rejects, in every year. A row of no known type counts
// as custom; a row without an id is one no update event can name.
export function lockReason(field: HolidayField, row: HolidayRow): string | null {
  if (!row.id) return NO_ID
  if (row.type !== 'LIB' && row.recurring) return CUSTOM_RECURRING_WARNING
  if (row.type === 'LIB' && (field === 'dates' || (field === 'recurring' && !row.recurring))) return LIB_WARNING
  return null
}

// The holiday form's date bounds.
export const FIRST_PICKABLE_YEAR = 2021

/**
 * A cell shown without an editor although nothing locks it, the holiday form's own limits: dates
 * outside 2021..next year offer no day to pick, and a past holiday cannot start repeating.
 */
export function cellReadOnly(field: HolidayField, row: HolidayRow, today: dayjs.Dayjs = utcToday()): boolean {
  const year = rowYear(row)
  if (field === 'dates') return year < FIRST_PICKABLE_YEAR || year > today.year() + 1
  if (field === 'recurring') return year < today.year() && !row.recurring && row.type !== 'LIB'
  return false
}

export interface CellAccessInput {
  field: HolidayField
  row: HolidayRow
  canEdit: boolean
  today?: dayjs.Dayjs
}

export interface CellAccess {
  editable: boolean
  lockReason?: string
}

/** A location the reader cannot edit shows the value only, with no lock. The Recurrence column says its locks in its own words. */
export function cellAccess({ field, row, canEdit, today = utcToday() }: CellAccessInput): CellAccess {
  if (!canEdit) return { editable: false }
  const readOnly = cellReadOnly(field, row, today)
  let reason = lockReason(field, row)
  if (field === 'recurring') reason = reason === LIB_WARNING ? IMPORTED_RECURRENCE_LOCKED : (reason ?? (readOnly ? PAST_YEAR_NO_REPEAT : null))
  return { editable: !readOnly, lockReason: reason ?? undefined }
}

/** A row's dates stay in its own year, so a move never crosses into a year that already has the holiday. */
export function datePickable(row: HolidayRow, day: dayjs.Dayjs, today: dayjs.Dayjs = utcToday()): boolean {
  const year = rowYear(row)
  return day.year() === year && year >= FIRST_PICKABLE_YEAR && year <= today.year() + 1
}

export interface HolidaySpan {
  startDate: string
  endDate: string
}

/**
 * The holiday form's overlap check, over every day of the span: the first other row of the year
 * that shares a day with it. The row itself, and its multi-day group or series, never count.
 */
export function overlappingHoliday(row: HolidayRow, span: HolidaySpan, yearRows: HolidayRow[]): HolidayRow | undefined {
  const start = holidayDay(span.startDate)
  const end = holidayDay(span.endDate)
  return yearRows.find((other) => !isSameHoliday(row, other) && holidayDay(other.date) <= end && holidayDay(other.endDate ?? other.date) >= start)
}

/** The row itself, its multi-day group or its series: never an overlap with itself. */
export function isSameHoliday(row: HolidayRow, other: HolidayRow): boolean {
  return (
    other === row ||
    (Boolean(row.id) && other.id === row.id) ||
    (Boolean(row.multiDayId) && other.multiDayId === row.multiDayId) ||
    (Boolean(row.recurringId) && other.recurringId === row.recurringId)
  )
}

/** location-year-holiday-deleted removes the whole series sharing a recurringId, in every year. */
export function deleteWarnsAboutSeries(row: HolidayRow): boolean {
  return Boolean(row.recurringId)
}

/** The series reaches years before this row's, whose leave the delete recalculates. */
export function seriesHasPastYears(row: HolidayRow, byYear: HolidayRowsByYear[]): boolean {
  if (!row.recurringId) return false
  const year = rowYear(row)
  return byYear.some((entry) => Number(entry.year) < year && entry.holidays.some((other) => other.recurringId === row.recurringId))
}

/** The other years holding a row with this row's id: the delete removes every row with that id. */
export function sameIdYears(row: HolidayRow, byYear: HolidayRowsByYear[]): number[] {
  if (!row.id) return []
  const year = rowYear(row)
  return byYear
    .filter((entry) => Number(entry.year) !== year && entry.holidays.some((other) => other.id === row.id))
    .map((entry) => Number(entry.year))
    .sort((a, b) => a - b)
}

export function holidayUpdatePayload({ row, locationId, patch }: HolidayUpdatePayloadInput): HolidayUpdatePayload {
  const datePatch = row.type === 'LIB' ? {} : patch
  const length = datePatch.length ?? holidayLength(row)
  const startDate = datePatch.startDate ?? row.date
  const endDate = length === 'MULTI_DAY' ? (datePatch.endDate ?? row.endDate ?? startDate) : startDate
  const recurring = patch.recurring ?? Boolean(row.recurring)
  return {
    eventType: 'LOCATION_HOLIDAY_UPDATED',
    eventGroup: 'LOCATION',
    id: row.id,
    locationId,
    name: patch.name ?? row.name,
    startDate,
    endDate,
    isHalfDay: length === 'HALF_DAY',
    recurring,
    ...(recurring
      ? {
          recurringRules: { exactDate: true },
          transferIfFallsOnNonWorkingDay: row.transferIfFallsOnNonWorkingDay ?? false,
        }
      : {}),
  }
}

export function patchIsComplete(row: HolidayRow, patch: HolidayPatch): boolean {
  const length = patch.length ?? holidayLength(row)
  if (length !== 'MULTI_DAY') return true
  const endDate = patch.endDate ?? row.endDate
  if (!endDate) return false
  const startDay = holidayDay(patch.startDate ?? row.date)
  const endDay = holidayDay(endDate)
  return endDay !== startDay && !dayjs(endDay).isBefore(dayjs(startDay), 'day')
}

// Nothing stores an imported holiday's library type yet, so every one reads Public until it does.
/** Imported rows don't store their library type, so it comes from the row's library entry when one is found. */
export function rowTag(row: HolidayRow, libraryType?: string): HolidayTag {
  return row.type === 'LIB' ? libraryTag(libraryType) : 'custom'
}

// An unknown library type reads Public rather than an untranslated tag.
export function libraryTag(type?: string): HolidayTag {
  return LIBRARY_TYPES.find((known) => known === type) ?? 'public'
}

/** The import wizard offered last, this and next year; the navigator never goes past next. */
export function importAllowed(year: number, today: dayjs.Dayjs = utcToday()): boolean {
  return year >= today.year() - 1
}

export function yearRange(byYear: IHolidaysByYear[], today: dayjs.Dayjs = utcToday()): YearRange {
  const thisYear = today.year()
  const earliest = Math.min(thisYear - 1, ...holidayYears(byYear))
  return { first: Math.max(EARLIEST_HOLIDAY_YEAR, earliest), last: thisYear + 1 }
}
